import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Spinner, ArrowLeft, ArrowRight, Target, Lightning, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

const INDUSTRIES = ["Auto Dealerships", "Construction Sites", "Real Estate", "Other"];
const QUESTIONS = [
  { key: "assets", q: "How many assets does the company manage?", options: ["Under 25", "25–99", "100–500", "500+"] },
  { key: "movement", q: "How often does inventory move?", options: ["Daily", "Weekly", "Rarely"] },
  { key: "locations", q: "How many locations?", options: ["One", "Multiple"] },
  { key: "tracking", q: "Current inventory tracking method", options: ["Paper, spreadsheets or handwritten labels", "Some software, but incomplete tracking", "Established inventory management system"] },
];
const EMPTY = { business_name: "", contact_name: "", email: "", phone: "", industry: "", assets: "", movement: "", locations: "", tracking: "", notes: "", website: "" };

const tierColor = (s) => (s >= 75 ? "text-emerald-600 border-emerald-500" : s >= 50 ? "text-amber-600 border-amber-500" : "text-zinc-500 border-zinc-400");

export default function ProspectScore() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pick = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || QUESTIONS.some((x) => !form[x.key])) return toast.error("Please name the business and answer all four questions");
    setLoading(true);
    try {
      const { data } = await api.post("/prospect-score", { ...form, email: form.email || null });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFB] text-zinc-950">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/orbit-icon.jpg" alt="Orbit" className="h-9 w-9 object-contain rounded-md" />
            <span className="font-display font-bold text-sm tracking-tight"><span className="sm:hidden">Orbit</span><span className="hidden sm:inline">Orbit Inventory Management</span></span>
          </Link>
          <Link to="/"><Button variant="ghost" className="h-10 font-semibold gap-2" data-testid="prospect-back-home"><ArrowLeft size={16} weight="bold" /> Back</Button></Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-12 lg:py-20 grid lg:grid-cols-2 gap-12 items-start">
        <div className="lg:sticky lg:top-24">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">Orbit Prospect Scoring Tool</p>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">Evaluate a business before you call</h1>
          <p className="mt-4 text-lg text-zinc-600 leading-relaxed max-w-md">Four quick questions produce a 0–100 priority score so you know who to contact first.</p>
          <div className="mt-8 space-y-4">
            {[
              { icon: Target, title: "Scored out of 100", desc: "Asset volume, movement, locations and current tracking method each add points." },
              { icon: Lightning, title: "Instant priority", desc: "75+ is High priority, 50–74 Medium, under 50 Low." },
              { icon: ChartBar, title: "Emailed to the Orbit team", desc: "Every scored business lands in your inbox with the full breakdown." },
            ].map((b) => (
              <div key={b.title} className="flex gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><b.icon size={20} weight="fill" className="text-emerald-600" /></div>
                <div><p className="font-semibold">{b.title}</p><p className="text-sm text-zinc-500">{b.desc}</p></div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 shadow-xl shadow-zinc-200/50" data-testid="prospect-form-card">
          {result ? (
            <div className="py-6 text-center" data-testid="prospect-result">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-400">Prospect priority score</p>
              <div className={`mx-auto mt-4 inline-flex flex-col items-center rounded-2xl border-4 px-10 py-6 ${tierColor(result.score)}`}>
                <div className="font-display text-6xl font-extrabold leading-none" data-testid="prospect-score">{result.score}<span className="text-2xl text-zinc-400">/100</span></div>
                <div className="font-bold text-lg mt-2" data-testid="prospect-tier">{result.tier}</div>
              </div>
              <h2 className="font-display text-2xl font-bold mt-6">{form.business_name}</h2>
              <ul className="mt-4 text-left text-sm divide-y divide-zinc-100 border border-zinc-200 rounded-lg overflow-hidden">
                {QUESTIONS.map((x) => (
                  <li key={x.key} className="flex items-center justify-between gap-3 px-4 py-2.5"><span className="text-zinc-600">{form[x.key]}</span><span className="font-bold text-zinc-900">+{result.breakdown[x.key]}</span></li>
                ))}
              </ul>
              <p className="text-zinc-500 text-sm mt-4">The Orbit team has been emailed this score.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <Button onClick={() => { setResult(null); setForm(EMPTY); }} variant="outline" className="h-12 px-6 border-zinc-300 font-semibold" data-testid="prospect-score-another">Score another business</Button>
                <Link to="/get-demo"><Button className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold w-full" data-testid="prospect-book-demo">Book a Demo</Button></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
              </div>
              <h2 className="font-display text-xl font-bold">About the business</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-sm font-semibold">Business Name *</Label>
                  <Input value={form.business_name} onChange={set("business_name")} className="h-11 mt-1.5" data-testid="prospect-business-name" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Contact Name</Label>
                  <Input value={form.contact_name} onChange={set("contact_name")} className="h-11 mt-1.5" data-testid="prospect-contact-name" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Industry</Label>
                  <Select value={form.industry} onValueChange={(v) => pick("industry", v)}>
                    <SelectTrigger className="h-11 mt-1.5" data-testid="prospect-industry"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{INDUSTRIES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-semibold">Email</Label>
                  <Input type="email" value={form.email} onChange={set("email")} className="h-11 mt-1.5" data-testid="prospect-email" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Phone</Label>
                  <Input value={form.phone} onChange={set("phone")} className="h-11 mt-1.5" data-testid="prospect-phone" />
                </div>
              </div>

              {QUESTIONS.map((x, i) => (
                <div key={x.key} data-testid={`prospect-q-${x.key}`}>
                  <Label className="text-sm font-semibold">{i + 1}. {x.q} *</Label>
                  <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: `repeat(${x.options.length > 3 ? 2 : 1}, minmax(0,1fr))` }}>
                    {x.options.map((o) => (
                      <button type="button" key={o} onClick={() => pick(x.key, o)} aria-pressed={form[x.key] === o} data-testid={`prospect-opt-${x.key}-${o.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                        className={`h-auto min-h-11 px-3 py-2 rounded-md border text-sm font-medium text-left transition-colors ${form[x.key] === o ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}>{o}</button>
                    ))}
                  </div>
                </div>
              ))}

              <div>
                <Label className="text-sm font-semibold">Notes <span className="font-normal text-zinc-400">(optional)</span></Label>
                <Textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-1.5" data-testid="prospect-notes" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2" data-testid="prospect-submit">
                {loading ? <Spinner size={18} className="animate-spin" /> : <>Get Prospect Score <ArrowRight size={18} weight="bold" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
